import React, { useEffect, useState, useRef, useMemo } from 'react';
import { FileUploadButton } from 'components/FileUpload/FileUploadButton';
import MediaGallery, { MediaGalleryFileInterface } from 'components/MediaGallery/MediaGallery';
import AddIcon from '@mui/icons-material/Add';
import styled from 'styled-components';
import { CreateStepInterface } from 'pages/Activities/activities.context';
import { useActivityWizardContext } from 'pages/Activities/ActivityWizard/activityWizard.context';
import PermissionCheck from 'components/Abilities/PermissionCheck';
import { useAuthContext } from 'context/auth.context';
import { arrayBufferToBase64 } from 'utils/media/arrayBufferToBase64';

const AddButtonContainer = styled.span`
  cursor: pointer;
  border-bottom: 1px solid;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.424rem;
  }
`;

export interface CustomFile extends File {
  encodedBuffer?: string;
}

const CustomFileUpload = ({
  step,
  stepIndex,
  onChange,
}: {
  step: CreateStepInterface;
  stepIndex: number;
  onChange: (files: File[]) => void;
}) => {
  const [files, setFiles] = useState<CustomFile[]>(step.files ?? []);
  const { newActivity, setNewActivity } = useActivityWizardContext();
  const { userTenantId } = useAuthContext();
  const filesRef = useRef(files);

  useEffect(() => {
    onChange(files);
    filesRef.current = files;
  }, [files]);

  const handleChange = async (file: CustomFile) => {
    file.encodedBuffer = await arrayBufferToBase64(file);
    setFiles([...files, file]);
    onChange(files);
  };

  const handleClickDelete = (selectedFile: MediaGalleryFileInterface) => {
    if (selectedFile.id !== undefined) {
      const removedMediaIndex = step.media?.findIndex((file, i) => file.id === selectedFile.id);

      if (removedMediaIndex !== undefined) {
        const media = [...step.media!];
        media.splice(removedMediaIndex, 1);
        const removedFileIds = [...(step.removedFileIds || [])];
        removedFileIds.push(selectedFile.id);

        const steps = [...newActivity.steps];
        steps[stepIndex] = { ...step, removedFileIds, media };

        setNewActivity({ ...newActivity, steps });
      }
    } else {
      const filesCopy = [...filesRef.current];
      const removedFileIndex = filesCopy.findIndex((file, i) => i === selectedFile.index);
      filesCopy.splice(removedFileIndex, 1);
      setFiles(filesCopy);
    }
  };

  const filesToDisplay: MediaGalleryFileInterface[] = [];

  files?.forEach((file, index) =>
    filesToDisplay.push({
      url: URL.createObjectURL(file),
      index,
      fileType: file.type,
    })
  );

  step.media?.forEach((file) =>
    filesToDisplay.push({
      url: file.signedUrl ?? '',
      id: file.id,
      fileType: file.fileType ?? '',
    })
  );

  const AddMediaButton = () => (
    <AddButtonContainer>
      <AddIcon />
      Add Media
    </AddButtonContainer>
  );

  const MemoizedGallery = useMemo((): JSX.Element => {
    return (
      <MediaGallery
        files={filesToDisplay}
        onClickDelete={handleClickDelete}
        sm={6}
        md={6}
        lg={6}
        xl={4}
        hideComments={true}
      />
    );
  }, [files.length, step.media?.length]);

  return (
    <>
      <PermissionCheck
        permission={{ action: 'create', subject: 'Media', conditions: { tenantId: userTenantId } }}
      >
        <FileUploadButton text={AddMediaButton()} onChange={handleChange} />
      </PermissionCheck>
      {MemoizedGallery}
    </>
  );
};

export default CustomFileUpload;
